import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
    Container, 
    Paper, 
    Typography, 
    Box,
    Button,
    Grid,
    Chip,
    Card,
    CardContent,
    CardHeader,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    SelectChangeEvent,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Collapse,
    FormControlLabel,
    Switch,
    InputAdornment
} from '@mui/material';
import { 
    ArrowBack,
    Edit as EditIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    CheckCircle as CheckCircleIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    AccountTree as AccountTreeIcon,
    Visibility as VisibilityIcon,
    TrendingUp as TrendingUpIcon,
    ExpandMore as ExpandMoreIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import { green } from '@mui/material/colors';
import EntityHierarchyGraph from '../../components/EntityHierarchyGraph/EntityHierarchyGraph';
import type { HierarchyEntity } from '../../components/EntityHierarchyGraph/EntityHierarchyGraph';
import TitleBar from '../../components/TitleBar/TitleBar';
import { formatDisplayDate } from '../../utils/dateUtils';
import {
    toDisplayThresholdValue,
    toStorageThresholdValue,
    formatThresholdBound,
} from '../../utils/metricThresholdUtils';
import { 
    createReviewEntity, 
    getUsers, 
    getReviewEntityTypes,
    getReviewEntity,
    updateReviewEntity,
    getMyReviewEntities,
    getReviewEntityAttributeKeys,
    type ReviewEntity,
    type AttributeKey,
    updateReviewEntityAttribute,
    deleteReviewEntityAttribute,
    getReviewEntityMetricThresholds,
    createMetricsOverride,
    updateMetricsOverride,
    type MetricsThreshold,
    type CreateOverrideRequest,
    type UpdateOverrideRequest,
    getReviewDefinitions,
    type ReviewDefinition,
    type GatedReview
} from '../../services/api';
import ReviewEntityMetricLevelsPanel from './ReviewEntityMetricLevelsPanel';

interface User {
    id: number;
    user_name: string;
    display_name?: string;
    roles: Role[];
}

interface Role {
    id: number;
    role_name: string;
}

interface EntityType {
    id: number;
    name: string;
}

interface ReviewFrequency {
    id: number;
    name: string;
    display_label: string;
    description: string;
}

interface EditState {
    name: boolean;
    description: boolean;
    org_entity_code: boolean;
    reviewers: boolean;
    reviewees: boolean;
    data_entry_supports: boolean;
    owner: boolean;
    status: boolean;
    valid_from: boolean;
    valid_till: boolean;
}

interface SavedState {
    name: boolean;
    description: boolean;
    org_entity_code: boolean;
    reviewers: boolean;
    reviewees: boolean;
    data_entry_supports: boolean;
    owner: boolean;
    status: boolean;
    valid_from: boolean;
    valid_till: boolean;
}


interface AttributeFormValues {
    [attributeName: string]: {
        value: string;
        description: string;
    };
}

const normalizeEntityAttributes = (entityData: ReviewEntity): ReviewEntity => {
    if (!entityData.review_attributes && entityData.attributes) {
        entityData.review_attributes = entityData.attributes;
    } else if (!entityData.review_attributes) {
        entityData.review_attributes = [];
    }
    if (!entityData.attributes) {
        entityData.attributes = entityData.review_attributes;
    }
    return entityData;
};

const buildAttributeFormValues = (
    keys: AttributeKey[],
    savedAttributes: { name: string; value: string; description: string }[] | undefined
): AttributeFormValues => {
    const values: AttributeFormValues = {};
    for (const key of keys) {
        const existing = savedAttributes?.find(attr => attr.name === key.name);
        values[key.name] = {
            value: existing?.value ?? '',
            description: existing?.description ?? key.description ?? '',
        };
    }
    return values;
};

const ReviewEntityForm: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [entity, setEntity] = useState<ReviewEntity | null>(null);
    const [showHierarchy, setShowHierarchy] = useState(false);
    const [hierarchyEntities, setHierarchyEntities] = useState<HierarchyEntity[]>([]);
    const [hierarchyLoading, setHierarchyLoading] = useState(false);

    // Form state
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [orgEntityCode, setOrgEntityCode] = useState('');
    const [entityTypeId, setEntityTypeId] = useState<number>(0);
    const [reviewers, setReviewers] = useState<number[]>([]);
    const [reviewees, setReviewees] = useState<number[]>([]);
    const [dataEntrySupports, setDataEntrySupports] = useState<number[]>([]);
    const [owner, setOwner] = useState<number | undefined>(undefined);
    const [status, setStatus] = useState('');
    const [validFrom, setValidFrom] = useState('');
    const [validTill, setValidTill] = useState('');

    // Data for dropdowns
    const [users, setUsers] = useState<User[]>([]);
    const [entityTypes, setEntityTypes] = useState<EntityType[]>([]);
    const [attributeKeys, setAttributeKeys] = useState<AttributeKey[]>([]);
    const [applicableAttributeKeys, setApplicableAttributeKeys] = useState<AttributeKey[]>([]);
    const [attributeFormValues, setAttributeFormValues] = useState<AttributeFormValues>({});
    const [attributesSaving, setAttributesSaving] = useState(false);
    const [attributesSaved, setAttributesSaved] = useState(false);
    const [attributesExpanded, setAttributesExpanded] = useState(false);
    const [attributeSearchQuery, setAttributeSearchQuery] = useState('');
    const [attributePage, setAttributePage] = useState(0);
    const [attributeRowsPerPage, setAttributeRowsPerPage] = useState(10);

    const [editState, setEditState] = useState<EditState>({
        name: false,
        description: false,
        org_entity_code: false,
        reviewers: false,
        reviewees: false,
        data_entry_supports: false,
        owner: false,
        status: false,
        valid_from: false,
        valid_till: false
    });
    
    const [savedState, setSavedState] = useState<SavedState>({
        name: false,
        description: false,
        org_entity_code: false,
        reviewers: false,
        reviewees: false,
        data_entry_supports: false,
        owner: false,
        status: false,
        valid_from: false,
        valid_till: false
    });

    // Metrics threshold state
    const [metricsThresholds, setMetricsThresholds] = useState<MetricsThreshold[]>([]);
    const [metricsLoading, setMetricsLoading] = useState(false);
    const [metricsExpanded, setMetricsExpanded] = useState(true);
    const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
    const [editOverrideDialogOpen, setEditOverrideDialogOpen] = useState(false);
    const [selectedThreshold, setSelectedThreshold] = useState<MetricsThreshold | null>(null);
    const [overrideForm, setOverrideForm] = useState({
        overriden_lower_bound: 0,
        overriden_upper_bound: 0,
        override_reason: '',
        override_is_critical: true,
        override_is_dashboard_metric: null as boolean | null,
        override_target_value: null as number | null,
        override_unit_of_measure: null as string | null
    });

    // Review definitions state
    const [allReviewDefinitions, setAllReviewDefinitions] = useState<ReviewDefinition[]>([]);
    const [reviewDefinitionsExpanded, setReviewDefinitionsExpanded] = useState(true);
    const [addReviewDefDialogOpen, setAddReviewDefDialogOpen] = useState(false);
    const [selectedNewReviewDefIds, setSelectedNewReviewDefIds] = useState<number[]>([]);

    // Gated reviews state (stored directly on review_entity)
    const [gatedReviewsDialogOpen, setGatedReviewsDialogOpen] = useState(false);
    const [gatedReviews, setGatedReviews] = useState<GatedReview[]>([]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError('');

                // Fetch all required data for dropdown options
                const [usersRes, entityTypesRes, attributeKeysRes, reviewDefsRes] = await Promise.all([
                    getUsers(),
                    getReviewEntityTypes(),
                    getReviewEntityAttributeKeys(),
                    getReviewDefinitions()
                ]);

                setUsers(usersRes.data);
                setEntityTypes(entityTypesRes.data);
                setAttributeKeys(attributeKeysRes.data);
                setAllReviewDefinitions(reviewDefsRes.data);

                // If id is provided, fetch the specific review entity
                if (id) {
                    console.log("Fetching data for review entity with ID:", id);
                    
                    // Fetch metrics thresholds for this entity
                    try {
                        setMetricsLoading(true);
                        const metricsResponse = await getReviewEntityMetricThresholds(Number(id));
                        setMetricsThresholds(metricsResponse.data);
                    } catch (err) {
                        console.error('Error fetching metrics thresholds:', err);
                    } finally {
                        setMetricsLoading(false);
                    }
                    try {
                        console.log("Fetching specific review entity data");
                        const reviewEntityResponse = await getReviewEntity(Number(id));
                        console.log("Received review entity data:", reviewEntityResponse.data);
                        
                        // Handle the new attributes structure
                        const entityData = normalizeEntityAttributes(reviewEntityResponse.data);
                        
                        setEntity(entityData);
                        
                        // Set form state with entity data
                        setName(entityData.name);
                        setDescription(entityData.description);
                        setOrgEntityCode(entityData.org_entity_code || '');
                        
                        // Set entity type ID directly from the response
                        if (entityData.review_entity_type_id) {
                            setEntityTypeId(entityData.review_entity_type_id);
                        }
                        
                        setReviewers(entityData.reviewers);
                        setReviewees(entityData.reviewees);
                        setDataEntrySupports(entityData.data_entry_supports || []);
                        setOwner(entityData.owner || undefined);
                        setStatus(entityData.status || 'ACTIVE');
                        setValidFrom(entityData.valid_from || '');
                        setValidTill(entityData.valid_till || '');
                        
                    } catch (err) {
                        console.error("Error fetching specific review entity:", err);
                        setError('Failed to fetch review entity details.');
                    }
                };

            } catch (err) {
                console.error('Error in fetchData:', err);
                setError('Failed to load required data');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id]);

    // Fetch metrics thresholds when entity changes
    const fetchMetricsThresholds = async () => {
        if (!entity?.id) return;
        
        try {
            setMetricsLoading(true);
            const response = await getReviewEntityMetricThresholds(entity.id);
            setMetricsThresholds(response.data);
        } catch (err) {
            console.error('Error fetching metrics thresholds:', err);
            setError('Failed to fetch metrics thresholds');
        } finally {
            setMetricsLoading(false);
        }
    };

    const handleOverrideClick = (threshold: MetricsThreshold) => {
        setSelectedThreshold(threshold);
        setOverrideForm({
            overriden_lower_bound: threshold.org_threshold_lower_bound,
            overriden_upper_bound: threshold.org_threshold_upper_bound,
            override_reason: '',
            override_is_critical: threshold.org_is_critical ?? threshold.is_critical ?? true,
            override_is_dashboard_metric: null,
            override_target_value: null,
            override_unit_of_measure: null
        });
        setOverrideDialogOpen(true);
    };

    const handleEditOverrideClick = (threshold: MetricsThreshold) => {
        setSelectedThreshold(threshold);
        setOverrideForm({
            overriden_lower_bound: threshold.overriden_lower_bound || threshold.org_threshold_lower_bound,
            overriden_upper_bound: threshold.overriden_upper_bound || threshold.org_threshold_upper_bound,
            override_reason: threshold.override_reason || '',
            override_is_critical: threshold.override_is_critical ?? threshold.is_critical ?? threshold.org_is_critical ?? true,
            override_is_dashboard_metric: threshold.override_is_dashboard_metric ?? null,
            override_target_value: threshold.override_target_value != null
                ? toDisplayThresholdValue(threshold.override_target_value, threshold.value_type) ?? null
                : null,
            override_unit_of_measure: threshold.override_unit_of_measure ?? null
        });
        setEditOverrideDialogOpen(true);
    };

    const handleCreateOverride = async () => {
        if (!selectedThreshold || !entity) return;

        try {
            const data: CreateOverrideRequest = {
                review_entity_id: entity.id,
                org_threshold_id: selectedThreshold.org_threshold_id,
                override_lower_bound: overrideForm.overriden_lower_bound,
                override_upper_bound: overrideForm.overriden_upper_bound,
                override_reason: overrideForm.override_reason,
                override_is_critical: overrideForm.override_is_critical,
                override_is_dashboard_metric: overrideForm.override_is_dashboard_metric,
                override_target_value: toStorageThresholdValue(
                    overrideForm.override_target_value,
                    selectedThreshold.value_type
                ),
                override_unit_of_measure: overrideForm.override_unit_of_measure?.trim() || null
            };

            await createMetricsOverride(data);
            await fetchMetricsThresholds(); // Refresh the data
            setOverrideDialogOpen(false);
            setSelectedThreshold(null);
        } catch (err) {
            console.error('Error creating override:', err);
            setError('Failed to create override');
        }
    };

    const handleUpdateOverride = async () => {
        if (!selectedThreshold || !entity) return;

        try {
            const data: UpdateOverrideRequest = {
                review_entity_id: entity.id,
                org_threshold_id: selectedThreshold.org_threshold_id,
                override_lower_bound: overrideForm.overriden_lower_bound,
                override_upper_bound: overrideForm.overriden_upper_bound,
                override_reason: overrideForm.override_reason,
                override_is_critical: overrideForm.override_is_critical,
                override_is_dashboard_metric: overrideForm.override_is_dashboard_metric,
                override_target_value: toStorageThresholdValue(
                    overrideForm.override_target_value,
                    selectedThreshold.value_type
                ),
                override_unit_of_measure: overrideForm.override_unit_of_measure?.trim() || null
            };

            await updateMetricsOverride(data);
            await fetchMetricsThresholds(); // Refresh the data
            setEditOverrideDialogOpen(false);
            setSelectedThreshold(null);
        } catch (err) {
            console.error('Error updating override:', err);
            setError('Failed to update override');
        }
    };

    const handleCloseOverrideDialog = () => {
        setOverrideDialogOpen(false);
        setEditOverrideDialogOpen(false);
        setSelectedThreshold(null);
        setOverrideForm({
            overriden_lower_bound: 0,
            overriden_upper_bound: 0,
            override_reason: '',
            override_is_critical: true,
            override_is_dashboard_metric: null,
            override_target_value: null,
            override_unit_of_measure: null
        });
    };

    useEffect(() => {
        const fetchApplicableAttributeKeys = async () => {
            try {
                if (entity?.review_entity_type_id) {
                    const response = await getReviewEntityAttributeKeys(entity.review_entity_type_id);
                    setApplicableAttributeKeys(response.data);
                    setAttributeFormValues(
                        buildAttributeFormValues(response.data, entity.review_attributes || entity.attributes)
                    );
                } else {
                    setApplicableAttributeKeys([]);
                    setAttributeFormValues({});
                }
            } catch (err) {
                console.error('Error fetching applicable attribute keys:', err);
                setError('Failed to fetch applicable attributes');
            }
        };

        fetchApplicableAttributeKeys();
    }, [entity?.review_entity_type_id, entity?.id]);

    const handleEntityTypeChange = (e: SelectChangeEvent<number>) => {
        const typeId = Number(e.target.value);
        setEntityTypeId(typeId);
        
        const selectedType = entityTypes.find(type => type.id === typeId);
        if (selectedType) {
            getReviewEntityAttributeKeys(selectedType.id)
                .then(response => setAttributeKeys(response.data))
                .catch(err => {
                    setError('Failed to load attribute keys');
                    console.error('Error fetching attribute keys:', err);
                });
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        const selectedType = entityTypes.find(type => type.id === entityTypeId);
        if (!selectedType) {
            setError('Invalid entity type selected');
            setLoading(false);
            return;
        }

        const data = {
            name,
            description,
            org_entity_code: orgEntityCode,
            review_entity_type_id: entityTypeId,
            reviewers,
            reviewees,
            data_entry_supports: dataEntrySupports,
            owner,
            parent_ids: [] as number[],
            review_definition_ids: [] as number[]
        };

        try {
            if (id) {
                await updateReviewEntity({
                    id: Number(id),
                    name: editState.name ? name : undefined,
                    description: editState.description ? description : undefined,
                    org_entity_code: editState.org_entity_code ? orgEntityCode : undefined,
                    review_entity_type_id: entityTypeId,
                    reviewers,
                    reviewees,
                    data_entry_supports: dataEntrySupports,
                    owner,
                    status,
                    valid_from: validFrom,
                    valid_till: validTill
                });
            } else {
                await createReviewEntity(data);
            }
            navigate('/review-entities');
        } catch (err) {
            setError('Failed to save review entity');
            console.error('Error saving review entity:', err);
        } finally {
            setLoading(false);
        }
    };

    const reviewerUsers = users.filter(user => 
        user.roles.some(role => role.role_name === 'REVIEWER')
    );

    const revieweeUsers = users.filter(user => 
        user.roles.some(role => role.role_name === 'REVIEWEE')
    );

    const dataEntrySupportUsers = users.filter(user =>
        user.roles.some(role => role.role_name === 'DATA_ENTRY_SUPPORT')
    );

    const handleEdit = (field: keyof EditState) => {
        setEditState((prevEditState) => ({ ...prevEditState, [field]: true }));
    };

    const handleSave = async (field: keyof SavedState) => {
        try {
            if (!id) return;

            let updateData = {};
            switch (field) {
                case 'name':
                    updateData = { name };
                    break;
                case 'description':
                    updateData = { description };
                    break;
                case 'org_entity_code':
                    updateData = { org_entity_code: orgEntityCode };
                    break;
                case 'reviewers':
                    updateData = { reviewers };
                    break;
                case 'reviewees':
                    updateData = { reviewees };
                    break;
                case 'data_entry_supports':
                    updateData = { data_entry_supports: dataEntrySupports };
                    break;
                case 'owner':
                    updateData = { owner };
                    break;
                case 'status':
                    updateData = { status };
                    break;
                case 'valid_from':
                    updateData = { valid_from: validFrom };
                    break;
                case 'valid_till':
                    updateData = { valid_till: validTill };
                    break;
            }

            await updateReviewEntity({
                id: Number(id),
                ...updateData
            });

            setSavedState((prevSavedState) => ({ ...prevSavedState, [field]: true }));
            setEditState((prevEditState) => ({ ...prevEditState, [field]: false }));
        } catch (err) {
            setError('Failed to update field');
            console.error('Error updating field:', err);
        }
    };

    const handleCancel = (field: keyof EditState) => {
        // Reset the field value to original value
        switch (field) {
            case 'name':
                setName(entity?.name || '');
                break;
            case 'description':
                setDescription(entity?.description || '');
                break;
            case 'org_entity_code':
                setOrgEntityCode(entity?.org_entity_code || '');
                break;
            case 'reviewers':
                setReviewers(entity?.reviewers || []);
                break;
            case 'reviewees':
                setReviewees(entity?.reviewees || []);
                break;
            case 'data_entry_supports':
                setDataEntrySupports(entity?.data_entry_supports || []);
                break;
            case 'owner':
                setOwner(entity?.owner || undefined);
                break;
            case 'status':
                setStatus(entity?.status || 'ACTIVE');
                break;
            case 'valid_from':
                setValidFrom(entity?.valid_from || '');
                break;
            case 'valid_till':
                setValidTill(entity?.valid_till || '');
                break;
        }
        // Exit edit mode
        setEditState(prev => ({ ...prev, [field]: false }));
    };

    const handleOpenAddReviewDefDialog = () => {
        setSelectedNewReviewDefIds([]);
        setAddReviewDefDialogOpen(true);
    };

    const handleCloseAddReviewDefDialog = () => {
        setAddReviewDefDialogOpen(false);
        setSelectedNewReviewDefIds([]);
    };

    const handleAddReviewDefinitions = async () => {
        if (!entity || selectedNewReviewDefIds.length === 0) return;
        
        try {
            // Combine existing and new review definition IDs
            const currentIds = entity.review_definition_ids || [];
            const updatedIds = Array.from(new Set([...currentIds, ...selectedNewReviewDefIds]));
            
            await updateReviewEntity({
                id: entity.id,
                review_definition_ids: updatedIds
            });
            
            // Refresh entity data to get updated review_definitions
            const response = await getReviewEntity(entity.id);
            setEntity(response.data);
            
            handleCloseAddReviewDefDialog();
        } catch (err) {
            console.error('Error adding review definitions:', err);
            setError('Failed to add review definitions');
        }
    };

    const handleDeleteReviewDefinition = async (reviewDefId: number) => {
        if (!entity) return;
        
        try {
            // Remove the review definition ID
            const currentIds = entity.review_definition_ids || [];
            const updatedIds = currentIds.filter(id => id !== reviewDefId);
            
            await updateReviewEntity({
                id: entity.id,
                review_definition_ids: updatedIds
            });
            
            // Refresh entity data to get updated review_definitions
            const response = await getReviewEntity(entity.id);
            setEntity(response.data);
        } catch (err) {
            console.error('Error deleting review definition:', err);
            setError('Failed to delete review definition');
        }
    };

    // Helper to check if a review definition is GATED.
    // Assigned ids that no longer resolve come back as null and must not crash the form.
    const isGatedReviewDef = (reviewDef: ReviewDefinition | null | undefined): boolean => {
        if (!reviewDef) {
            return false;
        }
        const reviewTypeName = reviewDef.review_type?.name || '';
        return reviewTypeName === 'GATED';
    };

    // Helper to check if a review definition is periodic (not GATED)
    const isPeriodicReviewDef = (
        reviewDef: ReviewDefinition | null | undefined
    ): reviewDef is ReviewDefinition => {
        if (!reviewDef) {
            return false;
        }
        return !isGatedReviewDef(reviewDef);
    };

    // Get only gated review definitions for the gated reviews dialog
    const gatedReviewDefinitions = allReviewDefinitions.filter(isGatedReviewDef);

    // Open gated reviews dialog
    const handleOpenGatedReviewsDialog = () => {
        if (!entity) return;
        // Load existing gated_reviews from entity
        setGatedReviews(entity.gated_reviews || []);
        setGatedReviewsDialogOpen(true);
    };

    const handleCloseGatedReviewsDialog = () => {
        setGatedReviewsDialogOpen(false);
        setGatedReviews([]);
    };

    const handleAddGatedReview = () => {
        const newReview: GatedReview = {
            name: '',
            schedule_cron: '',
            gate_name: null,
            review_definition_id: null
        };
        setGatedReviews(prev => [...prev, newReview]);
    };

    const handleRemoveGatedReview = (index: number) => {
        setGatedReviews(prev => prev.filter((_, i) => i !== index));
    };

    const handleGatedReviewChange = (index: number, field: keyof GatedReview, value: string | number | null) => {
        setGatedReviews(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    };

    // Gated review cron: "minute hour day month * year" — backend DateTrigger uses full local time.
    const parseGatedCron = (cron: string): { date: string; time: string } => {
        if (!cron?.trim()) return { date: '', time: '09:00' };
        const parts = cron.trim().split(/\s+/);
        if (parts.length < 6) return { date: '', time: '09:00' };
        const minute = parseInt(parts[0], 10);
        const hour = parseInt(parts[1], 10);
        const day = parts[2];
        const month = parts[3];
        const year = parts[5];
        if (day === '*' || month === '*' || year === '*' || Number.isNaN(minute) || Number.isNaN(hour)) {
            return { date: '', time: '09:00' };
        }
        const date = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        const time = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        return { date, time };
    };

    const buildGatedCron = (dateStr: string, timeStr: string): string => {
        if (!dateStr) return '';
        const date = new Date(dateStr + 'T12:00:00');
        const day = date.getDate();
        const month = date.getMonth() + 1;
        const year = date.getFullYear();
        let hour = 9;
        let minute = 0;
        const t = (timeStr || '').trim();
        if (t) {
            const [h, m] = t.split(':').map((x) => parseInt(x, 10));
            if (!Number.isNaN(h)) hour = Math.min(23, Math.max(0, h));
            if (!Number.isNaN(m)) minute = Math.min(59, Math.max(0, m));
        }
        return `${minute} ${hour} ${day} ${month} * ${year}`;
    };

    const handleSaveGatedReviews = async () => {
        if (!entity) return;
        
        // Filter out reviews without gate_name or schedule, and auto-generate name from gate_name if empty
        const validReviews = gatedReviews
            .filter(r => r.gate_name && r.schedule_cron)
            .map(r => ({
                ...r,
                name: r.name || r.gate_name || ''  // Use gate_name as name if name is empty
            }));
        
        try {
            await updateReviewEntity({
                id: entity.id,
                gated_reviews: validReviews.length > 0 ? validReviews : null
            });
            
            // Refresh entity data
            const response = await getReviewEntity(entity.id);
            setEntity(response.data);
            setGatedReviews(response.data.gated_reviews || []);
            handleCloseGatedReviewsDialog();
        } catch (err) {
            console.error('Error saving gated reviews:', err);
            setError('Failed to save gated reviews');
        }
    };

    const handleAttributeFormChange = (
        attributeName: string,
        field: 'value' | 'description',
        newValue: string
    ) => {
        setAttributeFormValues(prev => ({
            ...prev,
            [attributeName]: {
                ...prev[attributeName],
                [field]: newValue,
            },
        }));
        setAttributesSaved(false);
    };

    const filteredAttributeKeys = useMemo(() => {
        const query = attributeSearchQuery.trim().toLowerCase();
        const sorted = [...applicableAttributeKeys].sort((a, b) => {
            const nameA = (a.display_label || a.name || '').toLowerCase();
            const nameB = (b.display_label || b.name || '').toLowerCase();
            return nameA.localeCompare(nameB);
        });
        if (!query) {
            return sorted;
        }
        return sorted.filter(key => {
            const haystack = [key.name, key.display_label, key.description, key.data_type]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
            return haystack.includes(query);
        });
    }, [applicableAttributeKeys, attributeSearchQuery]);

    const paginatedAttributeKeys = useMemo(() => {
        const start = attributePage * attributeRowsPerPage;
        return filteredAttributeKeys.slice(start, start + attributeRowsPerPage);
    }, [filteredAttributeKeys, attributePage, attributeRowsPerPage]);

    useEffect(() => {
        setAttributePage(0);
    }, [attributeSearchQuery, applicableAttributeKeys]);

    useEffect(() => {
        const maxPage = Math.max(0, Math.ceil(filteredAttributeKeys.length / attributeRowsPerPage) - 1);
        if (attributePage > maxPage) {
            setAttributePage(maxPage);
        }
    }, [filteredAttributeKeys.length, attributeRowsPerPage, attributePage]);

    const handleSaveAttributes = async () => {
        if (!entity) return;

        setAttributesSaving(true);
        setError('');
        try {
            const existingNames = new Set(
                (entity.review_attributes || entity.attributes || []).map(attr => attr.name)
            );
            const attributesToSave = applicableAttributeKeys
                .map(key => {
                    const formValue = attributeFormValues[key.name];
                    const trimmedValue = (formValue?.value || '').trim();
                    if (!trimmedValue) {
                        return null;
                    }
                    return {
                        name: key.name,
                        value: trimmedValue,
                        description: (formValue?.description || '').trim() || key.description || '',
                    };
                })
                .filter((attr): attr is { name: string; value: string; description: string } => attr !== null);

            const attributesToDelete = applicableAttributeKeys
                .filter(key => {
                    const trimmedValue = (attributeFormValues[key.name]?.value || '').trim();
                    return !trimmedValue && existingNames.has(key.name);
                })
                .map(key => key.name);

            if (attributesToSave.length > 0) {
                await updateReviewEntityAttribute(entity.id, attributesToSave);
            }

            for (const attributeName of attributesToDelete) {
                await deleteReviewEntityAttribute(entity.id, attributeName);
            }

            const refreshedEntityData = await getReviewEntity(entity.id);
            const entityData = normalizeEntityAttributes(refreshedEntityData.data);
            setEntity(entityData);
            setAttributeFormValues(
                buildAttributeFormValues(applicableAttributeKeys, entityData.review_attributes)
            );
            setAttributesSaved(true);
            setTimeout(() => setAttributesSaved(false), 2000);
        } catch (err) {
            console.error('Error saving attributes:', err);
            setError('Failed to save attributes');
        } finally {
            setAttributesSaving(false);
        }
    };

    if (id && !entity) {
        return (
            <>
                <TitleBar />
                <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
                    <Paper sx={{ p: 2 }}>
                        {error ? (
                            <Typography color="error">{error}</Typography>
                        ) : (
                            <Typography>Loading...</Typography>
                        )}
                    </Paper>
                </Container>
            </>
        );
    }

    return (
        <>
            <TitleBar />
            <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
                <Paper sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                        <Button
                            variant="outlined"
                            startIcon={<ArrowBack />}
                            onClick={() => navigate('/review-entities')}
                        >
                            Back
                        </Button>
                        <Typography variant="h5" component="h2">
                            {id ? 'Review Entity Details' : 'Add Review Entity'}
                        </Typography>
                    </Box>

                    {error && (
                        <Typography color="error" sx={{ mb: 2 }}>
                            {error}
                        </Typography>
                    )}

                    {id && entity ? (
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Card sx={{ height: '100%', bgcolor: 'grey.50' }}>
                                    <CardHeader title="Basic Information" />
                                    <Divider />
                                    <CardContent>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    {editState.name ? (
                                                        <>
                                                            <TextField
                                                                fullWidth
                                                                label="Name"
                                                                value={name}
                                                                onChange={(e) => setName(e.target.value)}
                                                            />
                                                            <IconButton onClick={() => handleSave('name')} color="primary">
                                                                <SaveIcon />
                                                            </IconButton>
                                                            <IconButton onClick={() => handleCancel('name')} color="error">
                                                                <CancelIcon />
                                                            </IconButton>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Typography variant="body1">
                                                                <strong>Name:</strong> {name}
                                                            </Typography>
                                                            <IconButton onClick={() => handleEdit('name')} size="small">
                                                                <EditIcon />
                                                            </IconButton>
                                                            {savedState.name && (
                                                                <CheckCircleIcon sx={{ color: green[500] }} />
                                                            )}
                                                        </>
                                                    )}
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    {editState.description ? (
                                                        <>
                                                            <TextField
                                                                fullWidth
                                                                label="Description"
                                                                value={description}
                                                                onChange={(e) => setDescription(e.target.value)}
                                                                multiline
                                                                rows={2}
                                                            />
                                                            <IconButton onClick={() => handleSave('description')} color="primary">
                                                                <SaveIcon />
                                                            </IconButton>
                                                            <IconButton onClick={() => handleCancel('description')} color="error">
                                                                <CancelIcon />
                                                            </IconButton>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Typography variant="body1">
                                                                <strong>Description:</strong> {description}
                                                            </Typography>
                                                            <IconButton onClick={() => handleEdit('description')} size="small">
                                                                <EditIcon />
                                                            </IconButton>
                                                            {savedState.description && (
                                                                <CheckCircleIcon sx={{ color: green[500] }} />
                                                            )}
                                                        </>
                                                    )}
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    {editState.org_entity_code ? (
                                                        <>
                                                            <TextField
                                                                fullWidth
                                                                label="Organization Entity Code"
                                                                value={orgEntityCode}
                                                                onChange={(e) => setOrgEntityCode(e.target.value)}
                                                            />
                                                            <IconButton onClick={() => handleSave('org_entity_code')} color="primary">
                                                                <SaveIcon />
                                                            </IconButton>
                                                            <IconButton onClick={() => handleCancel('org_entity_code')} color="error">
                                                                <CancelIcon />
                                                            </IconButton>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Typography variant="body1">
                                                                <strong>Organization Entity Code:</strong> {orgEntityCode || 'Not set'}
                                                            </Typography>
                                                            <IconButton onClick={() => handleEdit('org_entity_code')} size="small">
                                                                <EditIcon />
                                                            </IconButton>
                                                            {savedState.org_entity_code && (
                                                                <CheckCircleIcon sx={{ color: green[500] }} />
                                                            )}
                                                        </>
                                                    )}
                                                </Box>
                                            </Grid>
                                            
                                            {/* Status Field */}
                                            <Grid item xs={12}>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    {editState.status ? (
                                                        <>
                                                            <FormControl fullWidth>
                                                                <InputLabel>Status</InputLabel>
                                                                <Select
                                                                    value={status}
                                                                    onChange={(e) => setStatus(e.target.value)}
                                                                    input={<OutlinedInput label="Status" />}
                                                                >
                                                                    <MenuItem value="DRAFT">DRAFT</MenuItem>
                                                                    <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                                                                    <MenuItem value="IN-ACTIVE">IN-ACTIVE</MenuItem>
                                                                </Select>
                                                            </FormControl>
                                                            <IconButton onClick={() => handleSave('status')} color="primary">
                                                                <SaveIcon />
                                                            </IconButton>
                                                            <IconButton onClick={() => handleCancel('status')} color="error">
                                                                <CancelIcon />
                                                            </IconButton>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Typography variant="body1">
                                                                <strong>Status:</strong> {status || 'ACTIVE'}
                                                            </Typography>
                                                            <IconButton onClick={() => handleEdit('status')} size="small">
                                                                <EditIcon />
                                                            </IconButton>
                                                            {savedState.status && (
                                                                <CheckCircleIcon sx={{ color: green[500] }} />
                                                            )}
                                                        </>
                                                    )}
                                                </Box>
                                            </Grid>
                                            
                                            {/* Valid From Field */}
                                            <Grid item xs={12} md={6}>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    {editState.valid_from ? (
                                                        <>
                                                            <TextField
                                                                fullWidth
                                                                label="Valid From"
                                                                type="date"
                                                                value={validFrom}
                                                                onChange={(e) => setValidFrom(e.target.value)}
                                                                InputLabelProps={{
                                                                    shrink: true,
                                                                }}
                                                                helperText="Format: DD/MM/YYYY"
                                                            />
                                                            <IconButton onClick={() => handleSave('valid_from')} color="primary">
                                                                <SaveIcon />
                                                            </IconButton>
                                                            <IconButton onClick={() => handleCancel('valid_from')} color="error">
                                                                <CancelIcon />
                                                            </IconButton>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Typography variant="body1">
                                                                <strong>Valid From:</strong> {validFrom ? formatDisplayDate(validFrom) : 'Not set'}  
                                                                {/* {validFrom ? new Date(validFrom).toLocaleDateString() : 'Not set'} */}
                                                            </Typography>
                                                            <IconButton onClick={() => handleEdit('valid_from')} size="small">
                                                                <EditIcon />
                                                            </IconButton>
                                                            {savedState.valid_from && (
                                                                <CheckCircleIcon sx={{ color: green[500] }} />
                                                            )}
                                                        </>
                                                    )}
                                                </Box>
                                            </Grid>
                                            
                                            {/* Valid Till Field */}
                                            <Grid item xs={12} md={6}>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    {editState.valid_till ? (
                                                        <>
                                                            <TextField
                                                                fullWidth
                                                                label="Valid Till"
                                                                type="date"
                                                                value={validTill}
                                                                onChange={(e) => setValidTill(e.target.value)}
                                                                InputLabelProps={{
                                                                    shrink: true,
                                                                }}
                                                            />
                                                            <IconButton onClick={() => handleSave('valid_till')} color="primary">
                                                                <SaveIcon />
                                                            </IconButton>
                                                            <IconButton onClick={() => handleCancel('valid_till')} color="error">
                                                                <CancelIcon />
                                                            </IconButton>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Typography variant="body1">
                                                                {/* <strong>Valid Till:</strong> {validTill ? new Date(validTill).toLocaleDateString() : 'Not set'} */}
                                                                <strong>Valid Till:</strong> {validTill ? formatDisplayDate(validTill) : 'Not set'}

                                                            </Typography>
                                                            <IconButton onClick={() => handleEdit('valid_till')} size="small">
                                                                <EditIcon />
                                                            </IconButton>
                                                            {savedState.valid_till && (
                                                                <CheckCircleIcon sx={{ color: green[500] }} />
                                                            )}
                                                        </>
                                                    )}
                                                </Box>
                                            </Grid>
                                            
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="subtitle1" color="textSecondary" fontWeight="bold">
                                                    Entity Type
                                                </Typography>
                                                <Typography variant="body1">
                                                    {entity.review_entity_type?.display_label || 'Unknown'}
                                                </Typography>
                                            </Grid>
                                            {entity.parents && entity.parents.length > 0 && (
                                                <Grid item xs={12}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Typography variant="subtitle1" color="textSecondary" fontWeight="bold">
                                                            Parent Entities
                                                        </Typography>
                                                        <IconButton 
                                                            size="small" 
                                                            color="primary" 
                                                            onClick={async () => {
                                                                setHierarchyLoading(true);
                                                                try {
                                                                    const res = await getMyReviewEntities();
                                                                    setHierarchyEntities(res.data as unknown as HierarchyEntity[]);
                                                                } catch (err) {
                                                                    console.error('Error fetching entities for hierarchy:', err);
                                                                    setHierarchyEntities([]);
                                                                } finally {
                                                                    setHierarchyLoading(false);
                                                                    setShowHierarchy(true);
                                                                }
                                                            }}
                                                            title="Visualize Entity Hierarchy"
                                                        >
                                                            <AccountTreeIcon />
                                                        </IconButton>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                                                        {entity.parents.map((parent: any) => (
                                                            <Chip
                                                                key={parent.id}
                                                                label={`${parent.name} (${parent.review_entity_type?.display_label || parent.review_entity_type?.name || 'Unknown'})`}
                                                                color="primary"
                                                                variant="outlined"
                                                                sx={{ mb: 1 }}
                                                            />
                                                        ))}
                                                    </Box>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Card sx={{ height: '100%', bgcolor: 'grey.50' }}>
                                    <CardHeader title="Review Settings" />
                                    <Divider />
                                    <CardContent>
                                        <Grid container spacing={2}>

                                            <Grid item xs={12}>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    {editState.reviewers ? (
                                                        <>
                                                            <FormControl fullWidth>
                                                                <InputLabel>Reviewers</InputLabel>
                                                                <Select
                                                                    multiple
                                                                    value={reviewers}
                                                                    onChange={(e: SelectChangeEvent<number[]>) => setReviewers(e.target.value as number[])}
                                                                    input={<OutlinedInput label="Reviewers" />}
                                                                    renderValue={(selected) => (
                                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                            {selected.map((value) => {
                                                                                const user = users.find(u => u.id === value);
                                                                                return <Chip key={value} label={user?.display_name || user?.user_name || value} />;
                                                                            })}
                                                                        </Box>
                                                                    )}
                                                                >
                                                                    {reviewerUsers.map((user) => (
                                                                        <MenuItem key={user.id} value={user.id}>
                                                                            {user.display_name || user.user_name}
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                            <IconButton onClick={() => handleSave('reviewers')} color="primary">
                                                                <SaveIcon />
                                                            </IconButton>
                                                            <IconButton onClick={() => handleCancel('reviewers')} color="error">
                                                                <CancelIcon />
                                                            </IconButton>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Box sx={{ flexGrow: 1 }}>
                                                                <Typography variant="body1">
                                                                    <strong>Reviewers:</strong>
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                                                                    {reviewers.map(id => {
                                                                        const user = users.find(u => u.id === id);
                                                                        return (
                                                                            <Chip
                                                                                key={id}
                                                                                label={user?.display_name || user?.user_name || id}
                                                                                color="secondary"
                                                                                variant="outlined"
                                                                            />
                                                                        );
                                                                    })}
                                                                </Box>
                                                            </Box>
                                                            <IconButton onClick={() => handleEdit('reviewers')} size="small">
                                                                <EditIcon />
                                                            </IconButton>
                                                            {savedState.reviewers && (
                                                                <CheckCircleIcon sx={{ color: green[500] }} />
                                                            )}
                                                        </>
                                                    )}
                                                </Box>
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    {editState.reviewees ? (
                                                        <>
                                                            <FormControl fullWidth>
                                                                <InputLabel>Reviewees</InputLabel>
                                                                <Select
                                                                    multiple
                                                                    value={reviewees}
                                                                    onChange={(e: SelectChangeEvent<number[]>) => setReviewees(e.target.value as number[])}
                                                                    input={<OutlinedInput label="Reviewees" />}
                                                                    renderValue={(selected) => (
                                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                            {selected.map((value) => {
                                                                                const user = users.find(u => u.id === value);
                                                                                return <Chip key={value} label={user?.display_name || user?.user_name || value} />;
                                                                            })}
                                                                        </Box>
                                                                    )}
                                                                >
                                                                    {revieweeUsers.map((user) => (
                                                                        <MenuItem key={user.id} value={user.id}>
                                                                            {user.display_name || user.user_name}
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                            <IconButton onClick={() => handleSave('reviewees')} color="primary">
                                                                <SaveIcon />
                                                            </IconButton>
                                                            <IconButton onClick={() => handleCancel('reviewees')} color="error">
                                                                <CancelIcon />
                                                            </IconButton>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Box sx={{ flexGrow: 1 }}>
                                                                <Typography variant="body1">
                                                                    <strong>Reviewees:</strong>
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                                                                    {reviewees.map(id => {
                                                                        const user = users.find(u => u.id === id);
                                                                        return (
                                                                            <Chip
                                                                                key={id}
                                                                                label={user?.display_name || user?.user_name || id}
                                                                                color="info"
                                                                                variant="outlined"
                                                                            />
                                                                        );
                                                                    })}
                                                                </Box>
                                                            </Box>
                                                            <IconButton onClick={() => handleEdit('reviewees')} size="small">
                                                                <EditIcon />
                                                            </IconButton>
                                                            {savedState.reviewees && (
                                                                <CheckCircleIcon sx={{ color: green[500] }} />
                                                            )}
                                                        </>
                                                    )}
                                                </Box>
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    {editState.data_entry_supports ? (
                                                        <>
                                                            <FormControl fullWidth>
                                                                <InputLabel>Data Entry Support</InputLabel>
                                                                <Select
                                                                    multiple
                                                                    value={dataEntrySupports}
                                                                    onChange={(e: SelectChangeEvent<number[]>) => setDataEntrySupports(e.target.value as number[])}
                                                                    input={<OutlinedInput label="Data Entry Support" />}
                                                                    renderValue={(selected) => (
                                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                            {selected.map((value) => {
                                                                                const user = users.find(u => u.id === value);
                                                                                return <Chip key={value} label={user?.display_name || user?.user_name || value} />;
                                                                            })}
                                                                        </Box>
                                                                    )}
                                                                >
                                                                    {dataEntrySupportUsers.map((user) => (
                                                                        <MenuItem key={user.id} value={user.id}>
                                                                            {user.display_name || user.user_name}
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                            <IconButton onClick={() => handleSave('data_entry_supports')} color="primary">
                                                                <SaveIcon />
                                                            </IconButton>
                                                            <IconButton onClick={() => handleCancel('data_entry_supports')} color="error">
                                                                <CancelIcon />
                                                            </IconButton>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Box sx={{ flexGrow: 1 }}>
                                                                <Typography variant="body1">
                                                                    <strong>Data Entry Support:</strong>
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary" display="block">
                                                                    Access includes this entity and all child entities recursively
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                                                                    {dataEntrySupports.map(id => {
                                                                        const user = users.find(u => u.id === id);
                                                                        return (
                                                                            <Chip
                                                                                key={id}
                                                                                label={user?.display_name || user?.user_name || id}
                                                                                color="warning"
                                                                                variant="outlined"
                                                                            />
                                                                        );
                                                                    })}
                                                                </Box>
                                                            </Box>
                                                            <IconButton onClick={() => handleEdit('data_entry_supports')} size="small">
                                                                <EditIcon />
                                                            </IconButton>
                                                            {savedState.data_entry_supports && (
                                                                <CheckCircleIcon sx={{ color: green[500] }} />
                                                            )}
                                                        </>
                                                    )}
                                                </Box>
                                            </Grid>
                                            
                                            {/* Owner Field */}
                                            <Grid item xs={12}>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    {editState.owner ? (
                                                        <>
                                                            <FormControl fullWidth>
                                                                <InputLabel>Owner</InputLabel>
                                                                <Select
                                                                    value={owner?.toString() || ''}
                                                                    onChange={(e: SelectChangeEvent<string>) => setOwner(e.target.value ? Number(e.target.value) : undefined)}
                                                                    input={<OutlinedInput label="Owner" />}
                                                                >
                                                                    <MenuItem value=""><em>No owner</em></MenuItem>
                                                                    {users.map((user) => (
                                                                        <MenuItem key={user.id} value={user.id}>
                                                                            {user.display_name || user.user_name}
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                            <IconButton onClick={() => handleSave('owner')} color="primary">
                                                                <SaveIcon />
                                                            </IconButton>
                                                            <IconButton onClick={() => handleCancel('owner')} color="error">
                                                                <CancelIcon />
                                                            </IconButton>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Typography variant="body1">
                                                                <strong>Owner:</strong> {owner ? (users.find(u => u.id === owner)?.display_name || users.find(u => u.id === owner)?.user_name || 'Unknown') : 'Not set'}
                                                            </Typography>
                                                            <IconButton onClick={() => handleEdit('owner')} size="small">
                                                                <EditIcon />
                                                            </IconButton>
                                                            {savedState.owner && (
                                                                <CheckCircleIcon sx={{ color: green[500] }} />
                                                            )}
                                                        </>
                                                    )}
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12}>
                                <Card sx={{ bgcolor: 'grey.50' }}>
                                    <CardHeader 
                                        title="Review Attributes" 
                                        subheader={
                                            applicableAttributeKeys.length === 0
                                                ? "No attributes defined for this entity type"
                                                : attributesExpanded
                                                    ? "Enter values for applicable attributes. Blank values are not saved."
                                                    : `${applicableAttributeKeys.length} attribute(s) available`
                                        }
                                        action={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {attributesExpanded && applicableAttributeKeys.length > 0 && (
                                                    <>
                                                        {attributesSaved && (
                                                            <CheckCircleIcon sx={{ color: green[500] }} />
                                                        )}
                                                        <Button
                                                            startIcon={<SaveIcon />}
                                                            onClick={handleSaveAttributes}
                                                            variant="contained"
                                                            color="primary"
                                                            disabled={attributesSaving}
                                                        >
                                                            {attributesSaving ? 'Saving...' : 'Save Attributes'}
                                                        </Button>
                                                    </>
                                                )}
                                                <IconButton
                                                    onClick={() => setAttributesExpanded(prev => !prev)}
                                                    aria-expanded={attributesExpanded}
                                                    aria-label="toggle review attributes"
                                                    sx={{
                                                        transform: attributesExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                                        transition: 'transform 0.3s'
                                                    }}
                                                >
                                                    <ExpandMoreIcon />
                                                </IconButton>
                                            </Box>
                                        }
                                    />
                                    <Divider />
                                    <Collapse in={attributesExpanded} timeout="auto" unmountOnExit>
                                    {applicableAttributeKeys.length > 0 && (
                                        <Box>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    gap: 2,
                                                    px: 2,
                                                    py: 1.5,
                                                    flexWrap: 'wrap',
                                                }}
                                            >
                                                <TextField
                                                    size="small"
                                                    placeholder="Search attributes…"
                                                    value={attributeSearchQuery}
                                                    onChange={(e) => setAttributeSearchQuery(e.target.value)}
                                                    sx={{ minWidth: 280, flexGrow: 1, maxWidth: 420 }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <SearchIcon fontSize="small" color="action" />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                                <Typography variant="body2" color="text.secondary">
                                                    {filteredAttributeKeys.length} of {applicableAttributeKeys.length} attributes
                                                </Typography>
                                            </Box>
                                            <TableContainer sx={{ maxHeight: 420 }}>
                                                <Table stickyHeader size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell sx={{ bgcolor: 'grey.100' }}>Attribute</TableCell>
                                                            <TableCell sx={{ bgcolor: 'grey.100' }}>Value</TableCell>
                                                            <TableCell sx={{ bgcolor: 'grey.100' }}>Description</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {paginatedAttributeKeys.length === 0 ? (
                                                            <TableRow>
                                                                <TableCell colSpan={3}>
                                                                    <Typography color="text.secondary" sx={{ py: 2 }}>
                                                                        No attributes match your search.
                                                                    </Typography>
                                                                </TableCell>
                                                            </TableRow>
                                                        ) : (
                                                            paginatedAttributeKeys.map((key) => {
                                                                const formValue = attributeFormValues[key.name] || {
                                                                    value: '',
                                                                    description: key.description || '',
                                                                };
                                                                return (
                                                                    <TableRow key={key.name} hover>
                                                                        <TableCell sx={{ minWidth: 160 }}>
                                                                            <Typography variant="body2" fontWeight={500}>
                                                                                {key.display_label || key.name}
                                                                            </Typography>
                                                                            {key.data_type && (
                                                                                <Typography variant="caption" color="text.secondary">
                                                                                    {key.data_type}
                                                                                </Typography>
                                                                            )}
                                                                        </TableCell>
                                                                        <TableCell sx={{ minWidth: 200 }}>
                                                                            {key.data_type === 'CATEGORICAL' &&
                                                                            (key.value_json?.categories?.length ?? 0) > 0 ? (
                                                                                <FormControl size="small" fullWidth>
                                                                                    <Select
                                                                                        displayEmpty
                                                                                        value={formValue.value}
                                                                                        onChange={(e) =>
                                                                                            handleAttributeFormChange(
                                                                                                key.name,
                                                                                                'value',
                                                                                                e.target.value as string
                                                                                            )
                                                                                        }
                                                                                    >
                                                                                        <MenuItem value="">
                                                                                            <em>Select value</em>
                                                                                        </MenuItem>
                                                                                        {(key.value_json?.categories || []).map(
                                                                                            (category) => (
                                                                                                <MenuItem
                                                                                                    key={category}
                                                                                                    value={category}
                                                                                                >
                                                                                                    {category}
                                                                                                </MenuItem>
                                                                                            )
                                                                                        )}
                                                                                    </Select>
                                                                                </FormControl>
                                                                            ) : (
                                                                                <TextField
                                                                                    size="small"
                                                                                    fullWidth
                                                                                    placeholder="Enter value"
                                                                                    value={formValue.value}
                                                                                    onChange={(e) =>
                                                                                        handleAttributeFormChange(
                                                                                            key.name,
                                                                                            'value',
                                                                                            e.target.value
                                                                                        )
                                                                                    }
                                                                                    type={
                                                                                        key.data_type === 'NUMERIC' ||
                                                                                        key.data_type === 'PERCENTAGE'
                                                                                            ? 'number'
                                                                                            : 'text'
                                                                                    }
                                                                                    inputProps={
                                                                                        key.data_type === 'NUMERIC' ||
                                                                                        key.data_type === 'PERCENTAGE'
                                                                                            ? {
                                                                                                  min: key.value_json?.min,
                                                                                                  max: key.value_json?.max,
                                                                                                  step:
                                                                                                      key.value_json?.precision !=
                                                                                                      null
                                                                                                          ? Math.pow(
                                                                                                                10,
                                                                                                                -Number(
                                                                                                                    key.value_json
                                                                                                                        .precision
                                                                                                                )
                                                                                                            )
                                                                                                          : undefined,
                                                                                              }
                                                                                            : undefined
                                                                                    }
                                                                                />
                                                                            )}
                                                                        </TableCell>
                                                                        <TableCell sx={{ minWidth: 240 }}>
                                                                            <TextField
                                                                                size="small"
                                                                                fullWidth
                                                                                placeholder="Optional description"
                                                                                value={formValue.description}
                                                                                onChange={(e) =>
                                                                                    handleAttributeFormChange(
                                                                                        key.name,
                                                                                        'description',
                                                                                        e.target.value
                                                                                    )
                                                                                }
                                                                            />
                                                                        </TableCell>
                                                                    </TableRow>
                                                                );
                                                            })
                                                        )}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                            <TablePagination
                                                component="div"
                                                count={filteredAttributeKeys.length}
                                                page={attributePage}
                                                onPageChange={(_, newPage) => setAttributePage(newPage)}
                                                rowsPerPage={attributeRowsPerPage}
                                                onRowsPerPageChange={(e) => {
                                                    setAttributeRowsPerPage(parseInt(e.target.value, 10));
                                                    setAttributePage(0);
                                                }}
                                                rowsPerPageOptions={[5, 10, 25, 50]}
                                            />
                                        </Box>
                                    )}
                                    </Collapse>
                                </Card>
                            </Grid>

                            {id && entity && (
                                <ReviewEntityMetricLevelsPanel
                                    entityId={entity.id}
                                    entityName={entity.name}
                                    typeMetricLevels={
                                        entity.review_entity_type?.applicable_metric_level_details || []
                                    }
                                />
                            )}

                            {/* Periodic Reviews Tile */}
                            <Grid item xs={12}>
                                <Card sx={{ bgcolor: 'grey.50' }}>
                                    <CardHeader 
                                        title="Periodic Reviews"
                                        subheader={entity.review_definitions?.filter(def => isPeriodicReviewDef(def)).length === 0 ? "No periodic reviews assigned" : undefined}
                                        action={
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Button
                                                    startIcon={<AddIcon />}
                                                    onClick={handleOpenAddReviewDefDialog}
                                                    variant="outlined"
                                                    size="small"
                                                >
                                                    Manage
                                                </Button>
                                                <IconButton
                                                    onClick={() => setReviewDefinitionsExpanded(!reviewDefinitionsExpanded)}
                                                    aria-expanded={reviewDefinitionsExpanded}
                                                    aria-label="show more"
                                                    sx={{
                                                        transform: reviewDefinitionsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                                        transition: 'transform 0.3s'
                                                    }}
                                                >
                                                    <ExpandMoreIcon />
                                                </IconButton>
                                            </Box>
                                        }
                                    />
                                    <Divider />
                                    <Collapse in={reviewDefinitionsExpanded} timeout="auto" unmountOnExit>
                                        <CardContent>
                                            {entity.review_definitions && entity.review_definitions.filter(def => isPeriodicReviewDef(def)).length > 0 ? (
                                                <TableContainer>
                                                    <Table>
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell>Name</TableCell>
                                                                <TableCell>Description</TableCell>
                                                                <TableCell>Type</TableCell>
                                                                <TableCell>Frequency</TableCell>
                                                                <TableCell align="right">Actions</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {entity.review_definitions.filter(def => isPeriodicReviewDef(def)).map((def) => (
                                                                <TableRow key={def.id}>
                                                                    <TableCell>{def.display_label}</TableCell>
                                                                    <TableCell>{def.description}</TableCell>
                                                                    <TableCell>{def.review_type?.display_label || '-'}</TableCell>
                                                                    <TableCell>{def.review_frequency?.display_label || '-'}</TableCell>
                                                                    <TableCell align="right">
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => handleDeleteReviewDefinition(def.id)}
                                                                            color="error"
                                                                            title="Delete"
                                                                        >
                                                                            <DeleteIcon />
                                                                        </IconButton>
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            ) : (
                                                <Typography color="textSecondary">
                                                    No periodic reviews assigned to this entity.
                                                </Typography>
                                            )}
                                        </CardContent>
                                    </Collapse>
                                </Card>
                            </Grid>

                            {/* Gated Reviews Section */}
                            <Grid item xs={12}>
                                <Card sx={{ bgcolor: 'grey.50' }}>
                                    <CardHeader 
                                        title="Gated Reviews"
                                        subheader={!entity.gated_reviews || entity.gated_reviews.length === 0 ? "No gated reviews scheduled" : undefined}
                                        action={
                                            <Button
                                                startIcon={<AddIcon />}
                                                onClick={handleOpenGatedReviewsDialog}
                                                variant="outlined"
                                                size="small"
                                            >
                                                Manage
                                            </Button>
                                        }
                                    />
                                    <Divider />
                                    <CardContent>
                                        {entity.gated_reviews && entity.gated_reviews.length > 0 ? (
                                            <TableContainer>
                                                <Table>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>Gate Name</TableCell>
                                                            <TableCell>Scheduled Date</TableCell>
                                                            <TableCell>Review Definition</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {entity.gated_reviews.map((review, index) => {
                                                            const reviewDef = allReviewDefinitions.find(d => d.id === review.review_definition_id);
                                                            const scheduledIso = parseGatedCron(review.schedule_cron).date;
                                                            return (
                                                                <TableRow key={index}>
                                                                    <TableCell>{review.gate_name || '-'}</TableCell>
                                                                    <TableCell>{scheduledIso ? formatDisplayDate(scheduledIso) : '-'}</TableCell>
                                                                    <TableCell>{reviewDef?.display_label || '-'}</TableCell>
                                                                </TableRow>
                                                            );
                                                        })}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        ) : (
                                            <Typography color="textSecondary">
                                                No gated reviews scheduled for this entity.
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Metrics Thresholds Tile */}
                            <Grid item xs={12}>
                                <Card sx={{ bgcolor: 'grey.50' }}>
                                    <CardHeader 
                                        title="Project Metrics Thresholds"
                                        action={
                                            <IconButton
                                                onClick={() => setMetricsExpanded(!metricsExpanded)}
                                                aria-expanded={metricsExpanded}
                                                aria-label="show more"
                                                sx={{
                                                    transform: metricsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                                    transition: 'transform 0.3s'
                                                }}
                                            >
                                                <ExpandMoreIcon />
                                            </IconButton>
                                        }
                                    />
                                    <Divider />
                                    <Collapse in={metricsExpanded} timeout="auto" unmountOnExit>
                                        {metricsLoading ? (
                                            <CardContent>
                                                <Typography>Loading metrics thresholds...</Typography>
                                            </CardContent>
                                        ) : metricsThresholds.length > 0 ? (
                                            <TableContainer>
                                                <Table>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>Threshold Name</TableCell>
                                                            <TableCell>Dimension</TableCell>
                                                            <TableCell>Org Lower Bound</TableCell>
                                                            <TableCell>Org Upper Bound</TableCell>
                                                            <TableCell>Org Critical</TableCell>
                                                            <TableCell>Org Dashboard</TableCell>
                                                            <TableCell>Org Target</TableCell>
                                                            <TableCell>Org UoM</TableCell>
                                                            <TableCell>First Applicable Level</TableCell>
                                                            <TableCell>Overridable</TableCell>
                                                            <TableCell>Override Lower</TableCell>
                                                            <TableCell>Override Upper</TableCell>
                                                            <TableCell>Critical</TableCell>
                                                            <TableCell>Dashboard</TableCell>
                                                            <TableCell>Target</TableCell>
                                                            <TableCell>UoM</TableCell>
                                                            <TableCell>Override Reason</TableCell>
                                                            <TableCell>Is Overridden</TableCell>
                                                            <TableCell>Created By</TableCell>
                                                            <TableCell>Created At</TableCell>
                                                            <TableCell>Actions</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {metricsThresholds.map((threshold) => (
                                                            <TableRow key={threshold.org_threshold_id}>
                                                                <TableCell>{threshold.threshold_name}</TableCell>
                                                                <TableCell>{threshold.threshold_dimension}</TableCell>
                                                                <TableCell>{threshold.org_threshold_lower_bound}</TableCell>
                                                                <TableCell>{threshold.org_threshold_upper_bound}</TableCell>
                                                                <TableCell>
                                                                    <Chip
                                                                        label={(threshold.org_is_critical ?? threshold.is_critical) ? 'Yes' : 'No'}
                                                                        color={(threshold.org_is_critical ?? threshold.is_critical) ? 'error' : 'default'}
                                                                        size="small"
                                                                    />
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Chip
                                                                        label={(threshold.org_is_dashboard_metric ?? threshold.is_dashboard_metric ?? true) ? 'Yes' : 'No'}
                                                                        color={(threshold.org_is_dashboard_metric ?? threshold.is_dashboard_metric ?? true) ? 'primary' : 'default'}
                                                                        size="small"
                                                                    />
                                                                </TableCell>
                                                                <TableCell>
                                                                    {formatThresholdBound(threshold.org_target_value, threshold.value_type)}
                                                                </TableCell>
                                                                <TableCell>{threshold.org_unit_of_measure || '—'}</TableCell>
                                                                <TableCell>
                                                                    {threshold.first_applicable_metric_level_display_label
                                                                        || threshold.first_applicable_metric_level_name
                                                                        || 'Any / finest'}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Chip 
                                                                        label={threshold.overridable ? 'Yes' : 'No'} 
                                                                        color={threshold.overridable ? 'success' : 'default'}
                                                                        size="small"
                                                                    />
                                                                </TableCell>
                                                                <TableCell>
                                                                    {threshold.is_overridden ? threshold.overriden_lower_bound : '-'}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {threshold.is_overridden ? threshold.overriden_upper_bound : '-'}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Chip
                                                                        label={threshold.is_critical ? 'Yes' : 'No'}
                                                                        color={threshold.is_critical ? 'error' : 'default'}
                                                                        size="small"
                                                                        variant={threshold.is_critical_overridden ? 'filled' : 'outlined'}
                                                                    />
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Chip
                                                                        label={(threshold.is_dashboard_metric ?? true) ? 'Yes' : 'No'}
                                                                        color={(threshold.is_dashboard_metric ?? true) ? 'primary' : 'default'}
                                                                        size="small"
                                                                        variant={threshold.is_dashboard_metric_overridden ? 'filled' : 'outlined'}
                                                                    />
                                                                </TableCell>
                                                                <TableCell>
                                                                    {threshold.is_target_value_overridden || threshold.target_value != null
                                                                        ? formatThresholdBound(threshold.target_value, threshold.value_type)
                                                                        : '-'}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {threshold.is_unit_of_measure_overridden
                                                                        ? (threshold.override_unit_of_measure || '—')
                                                                        : (threshold.unit_of_measure || '—')}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {threshold.is_overridden ? threshold.override_reason : '-'}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Chip 
                                                                        label={threshold.is_overridden ? 'Yes' : 'No'} 
                                                                        color={threshold.is_overridden ? 'warning' : 'default'}
                                                                        size="small"
                                                                    />
                                                                </TableCell>
                                                                <TableCell>{threshold.created_by_name || threshold.created_by}</TableCell>
                                                                <TableCell>{threshold.created_at}</TableCell>
                                                                <TableCell>
                                                                    {threshold.overridable && !threshold.is_overridden && (
                                                                        <Button
                                                                            size="small"
                                                                            variant="outlined"
                                                                            onClick={() => handleOverrideClick(threshold)}
                                                                        >
                                                                            Override
                                                                        </Button>
                                                                    )}
                                                                    {threshold.is_overridden && (
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => handleEditOverrideClick(threshold)}
                                                                            title="Edit Override"
                                                                        >
                                                                            <EditIcon />
                                                                        </IconButton>
                                                                    )}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        ) : (
                                            <CardContent>
                                                <Typography color="textSecondary">
                                                    No metrics thresholds available for this review entity.
                                                </Typography>
                                            </CardContent>
                                        )}
                                    </Collapse>
                                </Card>
                            </Grid>
                        </Grid>
                    ) : (
                        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
                                <TextField
                                    label="Name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    required
                                    fullWidth
                                    helperText={
                                        attributeKeys.find(key => key.name === name)?.description ||
                                        'Enter a name for the review entity'
                                    }
                                />
                                <TextField
                                    label="Description"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    required
                                    fullWidth
                                    multiline
                                    rows={4}
                                />
                            </Box>

                            <FormControl fullWidth required>
                                <InputLabel>Entity Type</InputLabel>
                                <Select
                                    value={entityTypeId}
                                    onChange={handleEntityTypeChange}
                                    label="Entity Type"
                                >
                                    {entityTypes.map((type) => (
                                        <MenuItem key={type.id} value={type.id}>
                                            {type.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl fullWidth required>
                                <InputLabel>Reviewers</InputLabel>
                                <Select
                                    multiple
                                    value={reviewers}
                                    onChange={(e: SelectChangeEvent<number[]>) => {
                                        setReviewers(e.target.value as number[]);
                                    }}
                                    input={<OutlinedInput label="Reviewers" />}
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map((value) => (
                                                <Chip 
                                                    key={value} 
                                                    label={users.find(u => u.id === value)?.user_name} 
                                                />
                                            ))}
                                        </Box>
                                    )}
                                >
                                    {reviewerUsers.map((user) => (
                                        <MenuItem key={user.id} value={user.id}>
                                            {user.user_name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl fullWidth required>
                                <InputLabel>Reviewees</InputLabel>
                                <Select
                                    multiple
                                    value={reviewees}
                                    onChange={(e: SelectChangeEvent<number[]>) => {
                                        setReviewees(e.target.value as number[]);
                                    }}
                                    input={<OutlinedInput label="Reviewees" />}
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map((value) => (
                                                <Chip 
                                                    key={value} 
                                                    label={users.find(u => u.id === value)?.user_name} 
                                                />
                                            ))}
                                        </Box>
                                    )}
                                >
                                    {revieweeUsers.map((user) => (
                                        <MenuItem key={user.id} value={user.id}>
                                            {user.user_name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl fullWidth>
                                <InputLabel>Data Entry Support</InputLabel>
                                <Select
                                    multiple
                                    value={dataEntrySupports}
                                    onChange={(e: SelectChangeEvent<number[]>) => {
                                        setDataEntrySupports(e.target.value as number[]);
                                    }}
                                    input={<OutlinedInput label="Data Entry Support" />}
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map((value) => (
                                                <Chip
                                                    key={value}
                                                    label={users.find(u => u.id === value)?.user_name}
                                                />
                                            ))}
                                        </Box>
                                    )}
                                >
                                    {dataEntrySupportUsers.map((user) => (
                                        <MenuItem key={user.id} value={user.id}>
                                            {user.user_name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>


                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    type="submit"
                                    disabled={loading}
                                >
                                    {loading ? 'Saving...' : 'Save'}
                                </Button>
                            </Box>
                        </Box>
                    )}
                </Paper>
            </Container>

            {/* Override Modal */}
            <Dialog open={overrideDialogOpen} onClose={handleCloseOverrideDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Override Metrics Threshold</DialogTitle>
                <DialogContent>
                    {selectedThreshold && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                            <Typography variant="body2" color="textSecondary">
                                <strong>Threshold:</strong> {selectedThreshold.threshold_name}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                <strong>Dimension:</strong> {selectedThreshold.threshold_dimension}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                <strong>Original Bounds:</strong> {selectedThreshold.org_threshold_lower_bound} to {selectedThreshold.org_threshold_upper_bound}
                            </Typography>
                            <TextField
                                label="Override Lower Bound"
                                type="number"
                                value={overrideForm.overriden_lower_bound}
                                onChange={(e) => setOverrideForm(prev => ({ ...prev, overriden_lower_bound: parseFloat(e.target.value) }))}
                                fullWidth
                                required
                            />
                            <TextField
                                label="Override Upper Bound"
                                type="number"
                                value={overrideForm.overriden_upper_bound}
                                onChange={(e) => setOverrideForm(prev => ({ ...prev, overriden_upper_bound: parseFloat(e.target.value) }))}
                                fullWidth
                                required
                            />
                            <TextField
                                label="Override Reason"
                                value={overrideForm.override_reason}
                                onChange={(e) => setOverrideForm(prev => ({ ...prev, override_reason: e.target.value }))}
                                fullWidth
                                multiline
                                rows={3}
                                required
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={Boolean(overrideForm.override_is_critical)}
                                        onChange={(e) => setOverrideForm(prev => ({ ...prev, override_is_critical: e.target.checked }))}
                                    />
                                }
                                label="Critical (trigger RAID on threshold violation)"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={overrideForm.override_is_dashboard_metric ?? (selectedThreshold.org_is_dashboard_metric ?? selectedThreshold.is_dashboard_metric ?? true)}
                                        onChange={(e) => setOverrideForm(prev => ({ ...prev, override_is_dashboard_metric: e.target.checked }))}
                                    />
                                }
                                label="Show on Dashboard"
                            />
                            <TextField
                                label={selectedThreshold.value_type === 'Percentage' ? 'Override Target (%)' : 'Override Target Value (optional)'}
                                type="number"
                                value={overrideForm.override_target_value ?? ''}
                                onChange={(e) => setOverrideForm(prev => ({
                                    ...prev,
                                    override_target_value: e.target.value === '' ? null : parseFloat(e.target.value)
                                }))}
                                fullWidth
                                helperText={`Org target: ${formatThresholdBound(selectedThreshold.org_target_value, selectedThreshold.value_type)}`}
                            />
                            <TextField
                                label="Override Unit of Measure (optional)"
                                value={overrideForm.override_unit_of_measure ?? ''}
                                onChange={(e) => setOverrideForm(prev => ({
                                    ...prev,
                                    override_unit_of_measure: e.target.value === '' ? null : e.target.value
                                }))}
                                fullWidth
                                placeholder="e.g. days, hours, FTE"
                                helperText={`Org UoM: ${selectedThreshold.org_unit_of_measure || '—'}`}
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseOverrideDialog} color="inherit">
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleCreateOverride}
                        variant="contained" 
                        color="primary"
                        disabled={!overrideForm.override_reason.trim()}
                    >
                        Create Override
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Override Modal */}
            <Dialog open={editOverrideDialogOpen} onClose={handleCloseOverrideDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Override</DialogTitle>
                <DialogContent>
                    {selectedThreshold && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                            <Typography variant="body2" color="textSecondary">
                                <strong>Threshold:</strong> {selectedThreshold.threshold_name}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                <strong>Dimension:</strong> {selectedThreshold.threshold_dimension}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                <strong>Original Bounds:</strong> {selectedThreshold.org_threshold_lower_bound} to {selectedThreshold.org_threshold_upper_bound}
                            </Typography>
                            <TextField
                                label="Override Lower Bound"
                                type="number"
                                value={overrideForm.overriden_lower_bound}
                                onChange={(e) => setOverrideForm(prev => ({ ...prev, overriden_lower_bound: parseFloat(e.target.value) }))}
                                fullWidth
                                required
                            />
                            <TextField
                                label="Override Upper Bound"
                                type="number"
                                value={overrideForm.overriden_upper_bound}
                                onChange={(e) => setOverrideForm(prev => ({ ...prev, overriden_upper_bound: parseFloat(e.target.value) }))}
                                fullWidth
                                required
                            />
                            <TextField
                                label="Override Reason"
                                value={overrideForm.override_reason}
                                onChange={(e) => setOverrideForm(prev => ({ ...prev, override_reason: e.target.value }))}
                                fullWidth
                                multiline
                                rows={3}
                                required
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={Boolean(overrideForm.override_is_critical)}
                                        onChange={(e) => setOverrideForm(prev => ({ ...prev, override_is_critical: e.target.checked }))}
                                    />
                                }
                                label="Critical (trigger RAID on threshold violation)"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={overrideForm.override_is_dashboard_metric ?? (selectedThreshold.is_dashboard_metric ?? selectedThreshold.org_is_dashboard_metric ?? true)}
                                        onChange={(e) => setOverrideForm(prev => ({ ...prev, override_is_dashboard_metric: e.target.checked }))}
                                    />
                                }
                                label="Show on Dashboard"
                            />
                            <TextField
                                label={selectedThreshold.value_type === 'Percentage' ? 'Override Target (%)' : 'Override Target Value (optional)'}
                                type="number"
                                value={overrideForm.override_target_value ?? ''}
                                onChange={(e) => setOverrideForm(prev => ({
                                    ...prev,
                                    override_target_value: e.target.value === '' ? null : parseFloat(e.target.value)
                                }))}
                                fullWidth
                                helperText={`Org target: ${formatThresholdBound(selectedThreshold.org_target_value, selectedThreshold.value_type)}`}
                            />
                            <TextField
                                label="Override Unit of Measure (optional)"
                                value={overrideForm.override_unit_of_measure ?? ''}
                                onChange={(e) => setOverrideForm(prev => ({
                                    ...prev,
                                    override_unit_of_measure: e.target.value === '' ? null : e.target.value
                                }))}
                                fullWidth
                                placeholder="e.g. days, hours, FTE"
                                helperText={`Org UoM: ${selectedThreshold.org_unit_of_measure || '—'}`}
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseOverrideDialog} color="inherit">
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleUpdateOverride}
                        variant="contained" 
                        color="primary"
                        disabled={!overrideForm.override_reason.trim()}
                    >
                        Update Override
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add Periodic Reviews Dialog */}
            <Dialog
                open={addReviewDefDialogOpen}
                onClose={handleCloseAddReviewDefDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Manage Periodic Reviews</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <FormControl fullWidth>
                            <InputLabel>Select Periodic Reviews</InputLabel>
                            <Select
                                multiple
                                value={selectedNewReviewDefIds}
                                onChange={(e) => setSelectedNewReviewDefIds(e.target.value as number[])}
                                input={<OutlinedInput label="Select Periodic Reviews" />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((value) => {
                                            const def = allReviewDefinitions.find(d => d.id === value);
                                            return (
                                                <Chip 
                                                    key={value} 
                                                    label={def?.display_label || def?.name || value}
                                                    size="small"
                                                />
                                            );
                                        })}
                                    </Box>
                                )}
                            >
                                {allReviewDefinitions
                                    .filter(def => isPeriodicReviewDef(def) && !entity?.review_definition_ids?.includes(def.id))
                                    .map((def) => (
                                        <MenuItem key={def.id} value={def.id}>
                                            <Box>
                                                <Typography variant="body1">{def.display_label}</Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {def.review_frequency?.display_label} - {def.description}
                                                </Typography>
                                            </Box>
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseAddReviewDefDialog} color="inherit">
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleAddReviewDefinitions}
                        variant="contained" 
                        color="primary"
                        disabled={selectedNewReviewDefIds.length === 0}
                    >
                        Add
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Gated Reviews Dialog */}
            <Dialog
                open={gatedReviewsDialogOpen}
                onClose={handleCloseGatedReviewsDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Manage Gated Reviews
                    <Typography variant="body2" color="textSecondary">
                        Add milestone-based gated reviews for this entity
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                            Add gated reviews with a date, optional time (defaults to 9:00 AM), and gate/milestone names.
                        </Typography>
                        
                        {gatedReviews.map((review, index) => (
                            <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                <TextField
                                    label="Gate Name"
                                    value={review.gate_name || ''}
                                    onChange={(e) => handleGatedReviewChange(index, 'gate_name', e.target.value)}
                                    placeholder="e.g., Design Complete"
                                    sx={{ flex: 1, minWidth: 150 }}
                                    required
                                />
                                <TextField
                                    type="date"
                                    label="Date"
                                    value={parseGatedCron(review.schedule_cron).date}
                                    onChange={(e) => {
                                        const { time } = parseGatedCron(review.schedule_cron);
                                        handleGatedReviewChange(index, 'schedule_cron', buildGatedCron(e.target.value, time));
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ flex: 1, minWidth: 150 }}
                                    required
                                />
                                <TextField
                                    type="time"
                                    label="Time (optional)"
                                    value={parseGatedCron(review.schedule_cron).time}
                                    onChange={(e) => {
                                        const { date } = parseGatedCron(review.schedule_cron);
                                        handleGatedReviewChange(index, 'schedule_cron', buildGatedCron(date, e.target.value));
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ flex: 1, minWidth: 140 }}
                                    helperText="Defaults to 9:00 if empty"
                                />
                                <FormControl sx={{ flex: 2, minWidth: 200 }}>
                                    <InputLabel>Review Definition</InputLabel>
                                    <Select
                                        value={review.review_definition_id || ''}
                                        onChange={(e) => handleGatedReviewChange(index, 'review_definition_id', e.target.value as number || null)}
                                        label="Review Definition"
                                    >
                                        <MenuItem value="">
                                            <em>None</em>
                                        </MenuItem>
                                        {gatedReviewDefinitions.map((def) => (
                                            <MenuItem key={def.id} value={def.id}>
                                                {def.display_label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <IconButton
                                    onClick={() => handleRemoveGatedReview(index)}
                                    color="error"
                                    size="small"
                                    sx={{ mt: 1 }}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Box>
                        ))}
                        
                        <Button
                            startIcon={<AddIcon />}
                            onClick={handleAddGatedReview}
                            variant="outlined"
                            size="small"
                        >
                            Add Gated Review
                        </Button>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseGatedReviewsDialog} color="inherit">
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleSaveGatedReviews}
                        variant="contained" 
                        color="primary"
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Entity Hierarchy Graph */}
            {showHierarchy && entity && (
                <EntityHierarchyGraph
                    entityId={entity.id}
                    allEntities={hierarchyEntities}
                    onClose={() => setShowHierarchy(false)}
                />
            )}
        </>
    );
};

export { ReviewEntityForm };